import * as fflate from 'fflate';
import { fabricJsonAssetsService } from './fabricJsonAssetsService';
import { photoAssetsService } from './photoAssetsService';

export const s3Service = {
  uploadTextArray,
  uploadBlob,
  uploadDataUrl,
  getDesignAssetUrl,
  readTextFile,
};

async function uploadTextArray(textArr, fileName) {
  const assets = [];
  for await (const [i, text] of textArr.entries()) {
    const buf = fflate.strToU8(text);
    const gzipped = fflate.gzipSync(buf, { level: 9 });
    const key = `${fileName}_0${i + 1}.txt`;
    const blob = new Blob([Array.from(gzipped).join(',')], {
      type: 'text/plain',
    });
    assets.push({
      key,
      photoRef: key,
      fileName: key,
      dirName: 'user-uploads',
      contentEncoding: 'gzip',
      contentType: 'text/plain',
      size: blob.size,
      blob,
    });
  }

  const response = await fabricJsonAssetsService.createPresignedUploads({
    uploadPrefix: fileName,
    assets: assets.map(({ blob, ...asset }) => asset),
  });
  const persisted = response?.assets || [];

  for (const asset of assets) {
    const match = persisted.find(item =>
      item && (item.photoRef === asset.photoRef || item.key === asset.key || item.fileName === asset.fileName)
    );
    await fabricJsonAssetsService.uploadToS3(match?.upload, asset.blob, asset.fileName);
  }

  return assets.map(asset => {
    const match = persisted.find(item =>
      item && (item.photoRef === asset.photoRef || item.key === asset.key)
    );
    return match?.s3Key || match?.key || asset.key;
  });
}

const DESIGN_BUCKET = process.env.GATSBY_S3_DESIGN_BUCKET;
const DESIGN_BUCKET_BASE_URL = process.env.GATSBY_S3_DESIGN_BUCKET_BASE_URL;

async function uploadBlob(blob, { dirName = 'user-uploads', fileName }) {
  if (!blob || !fileName) {
    throw new Error('blob and fileName are required for uploadBlob');
  }
  const response = await photoAssetsService.persistDesignAssets({
    uploadPrefix: dirName,
    assets: [
      {
        key: fileName,
        photoRef: fileName,
        fileName,
        dirName,
        contentType: blob.type || 'application/octet-stream',
        dataUrl: await blobToDataUrl(blob),
      },
    ],
  });
  const asset = response?.assets?.[0];
  const key = asset?.s3Key || asset?.key || fileName;
  return {
    key,
    url: asset?.url || getDesignAssetUrl(key),
  };
}

async function uploadDataUrl(dataUrl, options = {}) {
  if (typeof dataUrl !== 'string') {
    throw new Error('dataUrl string is required');
  }
  const blob = await dataUrlToBlob(dataUrl);
  return uploadBlob(blob, options);
}

function getDesignAssetUrl(key) {
  if (!key) return null;
  if (DESIGN_BUCKET_BASE_URL) {
    return `${DESIGN_BUCKET_BASE_URL}/${key}`;
  }
  const region = process.env.GATSBY_S3_REGION || 'us-east-1';
  return `https://${DESIGN_BUCKET}.s3.${region}.amazonaws.com/${key}`;
}

async function dataUrlToBlob(dataUrl) {
  try {
    if (typeof fetch === 'function') {
      const response = await fetch(dataUrl);
      return await response.blob();
    }
  } catch (error) {
    // ignore
  }
  const [header, body] = dataUrl.split(',');
  const match = header.match(/data:(.*?);base64/);
  const mimeType = match ? match[1] : 'image/jpeg';
  const binary =
    typeof window !== 'undefined' && typeof window.atob === 'function'
      ? window.atob(body)
      : Buffer.from(body, 'base64').toString('binary');
  const len = binary.length;
  const buffer = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    buffer[i] = binary.charCodeAt(i);
  }
  return new Blob([buffer], { type: mimeType });
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function readTextFile(bucket, key) {
  const directUrl =
    key == null
      ? bucket
      : `${getBucketBaseUrl(bucket)}/${String(key).replace(/^\//, '')}`;

  if (!directUrl) {
    throw new Error('A public S3 URL or bucket/key pair is required');
  }

  const response = await fetch(directUrl);
  if (!response.ok) {
    throw new Error(`Failed to read text file (${response.status})`);
  }
  return response.text();
}

function getBucketBaseUrl(bucketName) {
  if (!bucketName) return null;
  if (bucketName === DESIGN_BUCKET && DESIGN_BUCKET_BASE_URL) {
    return DESIGN_BUCKET_BASE_URL.replace(/\/$/, '');
  }
  const region = process.env.GATSBY_S3_REGION || 'us-east-1';
  return `https://${bucketName}.s3.${region}.amazonaws.com`;
}
